import { CompanionFace } from '@/components/companion/feed/CompanionFace';
import {
  COMPANION_PANEL_ID,
  focusCompanionTrigger,
} from '@/components/companion/feed/companion-panel-ids';
import { Box, Flex } from '@chakra-ui/react';
import { PanelHeader } from '@fluenceur/agent-blocks';
import { useCallback, type KeyboardEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { COMPANION_PANEL_WIDTH, type PanelMode } from './companion-panel-layout';

/**
 * The conversational panel's frame: header, a scrolling body, the composer at
 * the bottom. Beside the page (push) or over it (overlay) it is a named
 * complementary region the brand can ignore while working; on a phone-sized
 * window it is the whole screen, so it becomes a modal dialog and « Retour »
 * replaces the cross.
 */

/** Above the top bar (1200) and below the banners (1300), like the feed panel. */
const PANEL_Z_INDEX = 1250;
/** Full screen covers the banners too; Layout makes the chrome behind it inert. */
const FULLSCREEN_Z_INDEX = 1350;

/** Marks the panel's scrolling area, so the body can scroll it without moving the page. */
export const COMPANION_PANEL_SCROLL_ATTRIBUTE = 'data-companion-panel-scroll';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Sends Tab from the last focusable back to the first, and Shift+Tab the other way. */
function trapTab(event: KeyboardEvent<HTMLElement>): void {
  const focusable = [...event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => !element.closest('[inert]')
  );
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;
  const edge = event.shiftKey ? first : last;
  if (document.activeElement !== edge) return;
  event.preventDefault();
  (event.shiftKey ? last : first).focus();
}

interface CompanionPanelShellProps {
  mode: PanelMode;
  /** « Companion » on the home screen, then the conversation's first question. */
  title: string;
  /** `look` while the Companion works, `happy` once it has answered. */
  mood: 'look' | 'happy';
  body: ReactNode;
  composer: ReactNode;
  onClose: () => void;
  onNewConversation?: () => void;
  /** Top bar plus any banner above it, so a docked panel starts under them. */
  top?: number;
}

export const CompanionPanelShell = ({
  mode,
  title,
  mood,
  body,
  composer,
  onClose,
  onNewConversation,
  top = 0,
}: CompanionPanelShellProps) => {
  const fullscreen = mode === 'fullscreen';

  // The close is committed before the focus moves: in full screen the trigger
  // sits in the inert top bar until the panel is gone.
  const close = useCallback(() => {
    flushSync(onClose);
    focusCompanionTrigger();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.defaultPrevented || event.nativeEvent.isComposing) return;
      // From anywhere in the panel, unless something inside it (a menu, a
      // popover) already took this Escape, or it only ends an IME composition.
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      // Full screen is a modal dialog: Tab loops inside it instead of leaving
      // for the browser's own controls, the page behind being inert.
      if (event.key === 'Tab' && fullscreen) trapTab(event);
    },
    [close, fullscreen]
  );

  return (
    <Flex
      as="aside"
      id={COMPANION_PANEL_ID}
      aria-label="Companion"
      role={fullscreen ? 'dialog' : undefined}
      aria-modal={fullscreen ? true : undefined}
      data-mode={mode}
      direction="column"
      position="fixed"
      top={fullscreen ? 0 : `${top}px`}
      right={0}
      bottom={fullscreen ? undefined : 0}
      h={fullscreen ? '100dvh' : undefined}
      w={fullscreen ? '100%' : `${COMPANION_PANEL_WIDTH}px`}
      zIndex={fullscreen ? FULLSCREEN_Z_INDEX : PANEL_Z_INDEX}
      bg="agent.surface"
      color="agent.fg"
      borderLeftWidth={fullscreen ? 0 : '1px'}
      borderColor="agent.border"
      onKeyDown={handleKeyDown}
    >
      <PanelHeader
        face={<CompanionFace mood={mood} size={24} />}
        title={title}
        onNewConversation={onNewConversation}
        onClose={close}
        onBack={fullscreen ? close : undefined}
      />
      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        px="4.5"
        py="5"
        {...{ [COMPANION_PANEL_SCROLL_ATTRIBUTE]: '' }}
      >
        {body}
      </Box>
      <Box flexShrink={0} px="4" pt="2" pb="3">
        {composer}
      </Box>
    </Flex>
  );
};
